import React from 'react';
import PropTypes from 'prop-types';
import { useSiteContext } from '~/context/SiteContext';
import cn from 'classnames';
import { kebabCase } from 'lodash';

const Icons = ({ type, className, ariaLabel, onClick }) => {
  const { iconSet: set } = useSiteContext().theme;
  const sharedSvgAttrs = {
    className: cn(`icon-${kebabCase(type)}`, {
      [`${className}`]: className,
    }),
    'aria-hidden': true,
    focusable: false,
    role: 'presentation',
    xmlns: 'http://www.w3.org/2000/svg',
    fill: 'none',
    onClick,
    'aria-label': ariaLabel || type,
  };

  const iconSets = {
    fun: {
      account: (
        <svg {...sharedSvgAttrs} width="40" height="40" viewBox="0 0 40 40">
          <title>Account</title>
          <circle
            cx="20.0001"
            cy="16.0909"
            r="4.09091"
            stroke="currentColor"
            strokeWidth="2"
          />
          <path
            d="M27 29.4546C27 25.5886 23.866 22.4546 20 22.4546C16.134 22.4546 13 25.5886 13 29.4546"
            stroke="currentColor"
            strokeWidth="2"
          />
        </svg>
      ),
      cart: (
        <svg {...sharedSvgAttrs} width="40" height="40" viewBox="0 0 40 40">
          <title>Cart</title>
          <path
            d="M24.3635 14.3636C24.3635 11.9537 22.4098 10 19.9999 10C17.5899 10 15.6362 11.9537 15.6362 14.3636"
            stroke="currentColor"
            strokeWidth="2"
          />
          <rect
            x="10"
            y="14"
            width="20"
            height="14"
            rx="4"
            stroke="currentColor"
            strokeWidth="2"
          />
        </svg>
      ),
      close: (
        <svg {...sharedSvgAttrs} width="40" height="40" viewBox="0 0 40 40">
          <title>Close</title>
          <path
            d="M12.9289 13.0711L20 20.1421M20 20.1421L12.9289 27.2132M20 20.1421L27.0711 13.0711M27.0711 27.2132L23.5355 23.6777"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
      ),
      menu: (
        <svg {...sharedSvgAttrs} width="40" height="40" viewBox="0 0 40 40">
          <title>Menu</title>
          <line
            x1="14"
            y1="14"
            x2="27"
            y2="14"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <line
            x1="14"
            y1="19.2942"
            x2="27"
            y2="19.2942"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <line
            x1="14"
            y1="24.5881"
            x2="27"
            y2="24.5881"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ),
      search: (
        <svg {...sharedSvgAttrs} width="40" height="40" viewBox="0 0 40 40">
          <title>Search</title>
          <circle
            cx="19.4082"
            cy="19.4082"
            r="6.40816"
            stroke="currentColor"
            strokeWidth="2"
          />
          <path
            d="M26 27L28 29"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
      ),
    },
    minimal: {
      account: (
        <svg {...sharedSvgAttrs} viewBox="0 0 40 40">
          <title>Account</title>
          <circle
            cx="20.0001"
            cy="16.0909"
            r="4.09091"
            stroke="currentColor"
            strokeWidth="2"
          />
          <path
            d="M27 29.4546C27 25.5886 23.866 22.4546 20 22.4546C16.134 22.4546 13 25.5886 13 29.4546"
            stroke="currentColor"
            strokeWidth="2"
          />
        </svg>
      ),
      cart: (
        <svg {...sharedSvgAttrs} viewBox="0 0 40 40">
          <title>Cart</title>
          <path
            d="M24.3635 14.3636C24.3635 11.9537 22.4098 10 19.9999 10C17.5899 10 15.6362 11.9537 15.6362 14.3636"
            stroke="currentColor"
            strokeWidth="2"
          />
          <rect
            x="13"
            y="14.2728"
            width="14"
            height="14"
            stroke="currentColor"
            strokeWidth="2"
          />
        </svg>
      ),
      close: (
        <svg {...sharedSvgAttrs} viewBox="0 0 40 40">
          <title>Close</title>
          <path
            d="M12.9289 13.0711L20 20.1421M20 20.1421L12.9289 27.2132M20 20.1421L27.0711 27.2132M20 20.1421L27.0711 13.0711"
            stroke="currentColor"
            strokeWidth="2"
          />
        </svg>
      ),
      menu: (
        <svg {...sharedSvgAttrs} viewBox="0 0 40 40">
          <title>Menu</title>
          <line
            x1="13"
            y1="14"
            x2="28"
            y2="14"
            stroke="currentColor"
            strokeWidth="2"
          />
          <line
            x1="13"
            y1="19.2942"
            x2="28"
            y2="19.2942"
            stroke="currentColor"
            strokeWidth="2"
          />
          <line
            x1="13"
            y1="24.5881"
            x2="28"
            y2="24.5881"
            stroke="currentColor"
            strokeWidth="2"
          />
        </svg>
      ),
      search: (
        <svg {...sharedSvgAttrs} viewBox="0 0 40 40">
          <title>Search</title>
          <circle
            cx="19.4082"
            cy="19.4082"
            r="6.40816"
            stroke="currentColor"
            strokeWidth="2"
          />
          <path
            d="M24.459 24.7959L28.4998 28.8367"
            stroke="currentColor"
            strokeWidth="2"
          />
        </svg>
      ),
    },
    traditional: {
      account: (
        <svg {...sharedSvgAttrs} width="40" height="40" viewBox="0 0 40 40">
          <title>Account</title>
          <circle
            cx="20.0001"
            cy="16.0909"
            r="4.09091"
            stroke="currentColor"
            strokeWidth="2"
          />
          <path
            d="M27 29.4546C27 25.5886 23.866 22.4546 20 22.4546C16.134 22.4546 13 25.5886 13 29.4546"
            stroke="currentColor"
            strokeWidth="2"
          />
        </svg>
      ),
      cart: (
        <svg {...sharedSvgAttrs} width="40" height="40" viewBox="0 0 40 40">
          <title>Cart</title>
          <path
            d="M24.3635 14.3636C24.3635 11.9537 22.4098 10 19.9999 10C17.5899 10 15.6362 11.9537 15.6362 14.3636"
            stroke="currentColor"
            strokeWidth="2"
          />
          <rect
            x="13"
            y="14.2728"
            width="14"
            height="14"
            rx="4"
            stroke="currentColor"
            strokeWidth="2"
          />
        </svg>
      ),
      close: (
        <svg {...sharedSvgAttrs} width="40" height="40" viewBox="0 0 40 40">
          <title>Close</title>
          <path
            d="M12.9289 13.0711L20 20.1421M20 20.1421L12.9289 27.2132M20 20.1421L27.0711 27.2132M20 20.1421L27.0711 13.0711"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
      ),
      menu: (
        <svg {...sharedSvgAttrs} width="40" height="40" viewBox="0 0 40 40">
          <title>Menu</title>
          <line
            x1="13"
            y1="14"
            x2="28"
            y2="14"
            stroke="currentColor"
            strokeWidth="2"
          />
          <line
            x1="13"
            y1="19.2942"
            x2="28"
            y2="19.2942"
            stroke="currentColor"
            strokeWidth="2"
          />
          <line
            x1="13"
            y1="24.5881"
            x2="28"
            y2="24.5881"
            stroke="currentColor"
            strokeWidth="2"
          />
        </svg>
      ),
      search: (
        <svg {...sharedSvgAttrs} width="40" height="40" viewBox="0 0 40 40">
          <title>Search</title>
          <circle
            cx="19.4082"
            cy="19.4082"
            r="6.40816"
            stroke="currentColor"
            strokeWidth="2"
          />
          <path
            d="M24.459 24.7959L28.4998 28.8367"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
      ),
    },
  };

  const defaultIcons = {
    showingNow: (
      <svg
        width="33"
        height="22"
        viewBox="0 0 33 22"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M5.99845 1.26183L5.36845 0.631826C5.32659 0.589582 5.27649 0.556109 5.22115 0.533422C5.16581 0.510735 5.10638 0.499303 5.04643 0.499814C4.98649 0.500325 4.92727 0.512768 4.87233 0.536395C4.8174 0.560021 4.76789 0.594343 4.72678 0.637295C2.07456 3.45315 0.620117 7.11722 0.620117 11C0.620117 14.8828 2.07456 18.5469 4.72678 21.3628C4.76793 21.4057 4.81747 21.44 4.87242 21.4635C4.92737 21.4871 4.98661 21.4995 5.04655 21.5C5.1065 21.5004 5.16592 21.4889 5.22124 21.4662C5.27656 21.4435 5.32663 21.41 5.36845 21.3677L5.99845 20.7382C6.07939 20.656 6.12497 20.5462 6.12569 20.4317C6.12642 20.3171 6.08223 20.2068 6.00234 20.1235C3.67567 17.6418 2.39789 14.4185 2.39789 11C2.39789 7.58151 3.67567 4.35823 6.00234 1.87651C6.08223 1.79328 6.12642 1.68291 6.12569 1.5684C6.12497 1.45389 6.07939 1.34408 5.99845 1.26183ZM10.6612 5.03526L10.0335 4.4069C9.99134 4.36406 9.94077 4.33014 9.88486 4.30723C9.82895 4.28432 9.76888 4.27289 9.70832 4.27366C9.64776 4.27443 9.588 4.28737 9.53271 4.3117C9.47742 4.33602 9.42776 4.37121 9.38678 4.41511C7.74777 6.22847 6.84229 8.57117 6.84229 10.9984C6.84229 13.4256 7.74777 15.7683 9.38678 17.5817C9.42776 17.6256 9.47742 17.6608 9.53271 17.6851C9.588 17.7094 9.64776 17.7223 9.70832 17.7231C9.76888 17.7239 9.82895 17.7125 9.88486 17.6895C9.94077 17.6666 9.99134 17.6327 10.0335 17.5899L10.6612 16.961C10.7414 16.8788 10.7868 16.7696 10.7882 16.6557C10.7897 16.5417 10.747 16.4315 10.669 16.3474C9.34847 14.8685 8.62015 12.9668 8.62015 10.9978C8.62015 9.02885 9.34847 7.12722 10.669 5.64831C10.7458 5.56441 10.7876 5.45511 10.7862 5.3422C10.7847 5.2293 10.7401 5.12107 10.6612 5.03909V5.03526ZM28.514 0.637295C28.4729 0.594258 28.4233 0.559864 28.3684 0.536187C28.3134 0.512509 28.2541 0.500039 28.1941 0.499528C28.1341 0.499017 28.0746 0.510476 28.0192 0.533214C27.9638 0.555951 27.9136 0.589496 27.8718 0.631826L27.2418 1.26183C27.1608 1.34408 27.1153 1.45389 27.1145 1.5684C27.1138 1.68291 27.158 1.79328 27.2379 1.87651C29.5646 4.35823 30.8423 7.58151 30.8423 11C30.8423 14.4185 29.5646 17.6418 27.2379 20.1235C27.158 20.2068 27.1138 20.3171 27.1145 20.4317C27.1153 20.5462 27.1608 20.656 27.2418 20.7382L27.8718 21.3677C27.9136 21.41 27.9637 21.4436 28.0191 21.4664C28.0745 21.4892 28.134 21.5007 28.194 21.5003C28.254 21.4998 28.3133 21.4874 28.3683 21.4637C28.4233 21.4401 28.4729 21.4058 28.514 21.3628C31.1657 18.5469 32.6201 14.8828 32.6201 11C32.6201 7.11722 31.1657 3.45315 28.514 0.637295ZM16.6201 7.50003C15.9169 7.50003 15.2295 7.7053 14.6448 8.08989C14.06 8.47447 13.6043 9.0211 13.3352 9.66064C13.0661 10.3002 12.9957 11.0039 13.1329 11.6828C13.2701 12.3618 13.6087 12.9854 14.106 13.4749C14.6032 13.9644 15.2368 14.2977 15.9265 14.4328C16.6162 14.5678 17.3311 14.4985 17.9808 14.2336C18.6305 13.9687 19.1858 13.5201 19.5765 12.9445C19.9671 12.369 20.1757 11.6923 20.1757 11C20.1757 10.0718 19.8011 9.18153 19.1343 8.52516C18.4675 7.86878 17.5631 7.50003 16.6201 7.50003ZM16.6201 12.75C16.2685 12.75 15.9248 12.6474 15.6324 12.4551C15.3401 12.2628 15.1122 11.9895 14.9777 11.6697C14.8431 11.35 14.8079 10.9981 14.8765 10.6586C14.9451 10.3192 15.1144 10.0073 15.363 9.76259C15.6117 9.51785 15.9284 9.35118 16.2733 9.28366C16.6181 9.21613 16.9756 9.25079 17.3004 9.38324C17.6253 9.51569 17.9029 9.74 18.0983 10.0278C18.2936 10.3156 18.3979 10.6539 18.3979 11C18.3979 11.4642 18.2106 11.9093 17.8772 12.2375C17.5438 12.5657 17.0916 12.75 16.6201 12.75ZM23.2068 4.40855L22.579 5.03909C22.4989 5.1213 22.4535 5.23044 22.452 5.34438C22.4506 5.45831 22.4932 5.56853 22.5712 5.65269C23.8918 7.1316 24.6201 9.03323 24.6201 11.0022C24.6201 12.9712 23.8918 14.8728 22.5712 16.3517C22.4932 16.4359 22.4506 16.5461 22.452 16.6601C22.4535 16.774 22.4989 16.8831 22.579 16.9653L23.2068 17.5937C23.2489 17.6365 23.2995 17.6705 23.3554 17.6934C23.4113 17.7163 23.4714 17.7277 23.5319 17.7269C23.5925 17.7262 23.6522 17.7132 23.7075 17.6889C23.7628 17.6646 23.8125 17.6294 23.8535 17.5855C25.4925 15.7721 26.3979 13.4294 26.3979 11.0022C26.3979 8.575 25.4925 6.2323 23.8535 4.41894C23.8126 4.3749 23.7631 4.33953 23.7079 4.31502C23.6527 4.29051 23.593 4.27736 23.5324 4.27639C23.4718 4.27541 23.4117 4.28663 23.3557 4.30936C23.2997 4.33209 23.249 4.36584 23.2068 4.40855Z"
          fill="#EB5757"
        />
      </svg>
    ),
    check: (
      <svg {...sharedSvgAttrs} width="18" height="18" viewBox="0 0 18 18">
        <title>Check</title>
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M15.7072 5.20712L7.00008 13.9142L2.29297 9.20712L3.70718 7.79291L7.00008 11.0858L14.293 3.79291L15.7072 5.20712Z"
          fill="currentColor"
        />
      </svg>
    ),
    up: (
      <svg {...sharedSvgAttrs} viewBox="0 0 25 25">
        <title>Up</title>
        <path
          d="M12.5 10.5L16.3971 13.875H8.60289L12.5 10.5Z"
          fill="currentColor"
        />
      </svg>
    ),
    down: (
      <svg {...sharedSvgAttrs} viewBox="0 0 25 25">
        <title>Down</title>
        <path
          d="M12.5 15L8.60289 11.625L16.3971 11.625L12.5 15Z"
          fill="currentColor"
        />
      </svg>
    ),
    left: (
      <svg {...sharedSvgAttrs} viewBox="0 0 40 40">
        <title>Left</title>
        <path
          d="M23 27L15.9289 19.9289L23 12.8579"
          stroke="currentColor"
          strokeWidth="2"
        />
      </svg>
    ),
    right: (
      <svg {...sharedSvgAttrs} viewBox="0 0 40 40">
        <title>Right</title>
        <path
          d="M18 13L25.0711 20.0711L18 27.1421"
          stroke="currentColor"
          strokeWidth="2"
        />
      </svg>
    ),
    plus: (
      <svg {...sharedSvgAttrs} viewBox="0 0 25 25">
        <title>Plus</title>
        <path
          d="M13 9L13 13M13 13L9 13M13 13L13 17M13 13L17 13"
          stroke="currentColor"
          strokeWidth="1.25"
        />
      </svg>
    ),
    minus: (
      <svg {...sharedSvgAttrs} viewBox="0 0 25 25">
        <title>Minus</title>
        <path d="M9 12L13 12L17 12" stroke="currentColor" strokeWidth="1.25" />
      </svg>
    ),
    playcircle: (
      <svg {...sharedSvgAttrs} viewBox="0 0 28 28">
        <title>Play</title>
        <circle opacity="0.8" cx="14" cy="14" r="14" fill="white" />
        <path
          d="M20.5 13.134C21.1667 13.5189 21.1667 14.4811 20.5 14.866L11.5 20.0622C10.8333 20.4471 10 19.966 10 19.1962L10 8.80385C10 8.03405 10.8333 7.55292 11.5 7.93782L20.5 13.134Z"
          fill="currentColor"
        />
      </svg>
    ),
    pausecircle: (
      <svg {...sharedSvgAttrs} viewBox="0 0 28 28">
        <title>Pause</title>
        <circle opacity="0.8" cx="14" cy="14" r="14" fill="white" />
        <rect x="9" y="9" width="3" height="10" rx="1" fill="currentColor" />
        <rect x="16" y="9" width="3" height="10" rx="1" fill="currentColor" />
      </svg>
    ),
    remove: (
      <svg {...sharedSvgAttrs} viewBox="0 0 40 40">
        <title>Remove</title>
        <path
          d="M16 17H24V26C24 26.5523 23.5523 27 23 27H17C16.4477 27 16 26.5523 16 26V17Z"
          stroke="currentColor"
          strokeWidth="2"
        />
        <path
          d="M17 17C17 15.3431 18.3431 14 20 14C21.6569 14 23 15.3431 23 17H17Z"
          stroke="currentColor"
          strokeWidth="2"
        />
        <path d="M26 17H14" stroke="currentColor" strokeWidth="2" />
      </svg>
    ),
    visa: (
      <svg {...sharedSvgAttrs} width="34" height="24" viewBox="0 0 34 24">
        <rect
          x="0.5"
          y="0.5"
          width="33"
          height="23"
          rx="3.5"
          fill="white"
          stroke="#D9D9D9"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M10.7501 15.8582H8.69031L7.14576 9.79235C7.07245 9.51332 6.91679 9.26664 6.68782 9.15038C6.11639 8.85821 5.48672 8.62568 4.7998 8.50841V8.27487H8.11789C8.57583 8.27487 8.91929 8.62568 8.97653 9.0331L9.77793 13.4086L11.8367 8.27487H13.8392L10.7501 15.8582ZM14.984 15.8582H13.0388L14.6406 8.27487H16.5858L14.984 15.8582ZM19.1025 10.3757C19.1597 9.96725 19.5032 9.73372 19.9039 9.73372C20.5336 9.67508 21.2195 9.79235 21.7919 10.0835L22.1354 8.45079C21.5629 8.21725 20.9333 8.09998 20.3619 8.09998C18.4738 8.09998 17.1 9.15038 17.1 10.6082C17.1 11.7173 18.0731 12.2996 18.7601 12.6504C19.5032 13.0002 19.7894 13.2337 19.7322 13.5835C19.7322 14.1082 19.1597 14.3418 18.5883 14.3418C17.9014 14.3418 17.2145 14.1669 16.5858 13.8747L16.2424 15.5084C16.9293 15.7996 17.6724 15.9169 18.3594 15.9169C20.4763 15.9745 21.7919 14.9251 21.7919 13.35C21.7919 11.3664 19.1025 11.2502 19.1025 10.3757ZM28.5998 15.8582L27.0553 8.27487H25.3962C25.0528 8.27487 24.7093 8.50841 24.5948 8.85821L21.7347 15.8582H23.7372L24.1369 14.7502H26.5973L26.8263 15.8582H28.5998ZM25.6824 10.3171L26.2539 13.1751H24.6521L25.6824 10.3171Z"
          fill="#172B85"
        />
      </svg>
    ),
    mastercard: (
      <svg {...sharedSvgAttrs} width="34" height="24" viewBox="0 0 34 24">
        <rect
          x="0.5"
          y="0.5"
          width="33"
          height="23"
          rx="3.5"
          fill="white"
          stroke="#D9D9D9"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M17.179 16.8295C15.9949 17.8275 14.459 18.43 12.7807 18.43C9.03582 18.43 6 15.4303 6 11.73C6 8.02972 9.03582 5.03003 12.7807 5.03003C14.459 5.03003 15.9949 5.63253 17.179 6.63057C18.363 5.63253 19.8989 5.03003 21.5773 5.03003C25.3221 5.03003 28.358 8.02972 28.358 11.73C28.358 15.4303 25.3221 18.43 21.5773 18.43C19.8989 18.43 18.363 17.8275 17.179 16.8295Z"
          fill="#ED0006"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M17.1787 16.8295C18.6366 15.6006 19.5611 13.772 19.5611 11.73C19.5611 9.68807 18.6366 7.85947 17.1787 6.63057C18.3628 5.63253 19.8987 5.03003 21.577 5.03003C25.3219 5.03003 28.3577 8.02972 28.3577 11.73C28.3577 15.4303 25.3219 18.43 21.577 18.43C19.8987 18.43 18.3628 17.8275 17.1787 16.8295Z"
          fill="#F9A000"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M17.1793 16.8295C18.6372 15.6006 19.5616 13.772 19.5616 11.7301C19.5616 9.68811 18.6372 7.85952 17.1793 6.63062C15.7213 7.85952 14.7969 9.68811 14.7969 11.7301C14.7969 13.772 15.7213 15.6006 17.1793 16.8295Z"
          fill="#FF5E00"
        />
      </svg>
    ),
    amex: (
      <svg {...sharedSvgAttrs} width="34" height="24" viewBox="0 0 34 24">
        <rect width="34" height="24" rx="4" fill="#1F72CD" />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M6.09517 8.5L2.91406 15.7467H6.7223L7.19441 14.5913H8.27355L8.74566 15.7467H12.9375V14.8649L13.311 15.7467H15.4793L15.8528 14.8462V15.7467H24.5706L25.6307 14.6213L26.6232 15.7467L31.1009 15.7561L27.9097 12.1436L31.1009 8.5H26.6927L25.6608 9.60463L24.6995 8.5H15.2156L14.4013 10.3704L13.5678 8.5H9.7675V9.35186L9.34474 8.5H6.09517ZM6.83205 9.52905H8.68836L10.7984 14.4431V9.52905H12.8319L14.4617 13.0524L15.9637 9.52905H17.987V14.7291H16.7559L16.7458 10.6544L14.9509 14.7291H13.8495L12.0446 10.6544V14.7291H9.51179L9.03162 13.5633H6.43745L5.95827 14.728H4.60123L6.83205 9.52905ZM24.1196 9.52905H19.1134V14.726H24.0421L25.6307 13.0036L27.1618 14.726H28.7624L26.436 12.1426L28.7624 9.52905H27.2313L25.6507 11.2316L24.1196 9.52905ZM7.73508 10.4089L6.8804 12.4856H8.58876L7.73508 10.4089ZM20.3497 11.555V10.6057V10.6048H23.4734L24.8364 12.1229L23.413 13.6493H20.3497V12.613H23.0808V11.555H20.3497Z"
          fill="white"
        />
      </svg>
    ),
    paypal: (
      <svg {...sharedSvgAttrs} width="34" height="24" viewBox="0 0 34 24">
        <rect
          x="0.5"
          y="0.5"
          width="33"
          height="23"
          rx="3.5"
          fill="white"
          stroke="#D9D9D9"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M14.614 18.4483L14.8347 16.9992L14.3431 16.9873H11.9951L13.6268 6.2937C13.6319 6.26132 13.6484 6.23126 13.6724 6.20987C13.6965 6.18849 13.7272 6.17676 13.7594 6.17676H17.7184C19.0328 6.17676 19.9398 6.45939 20.4133 7.01734C20.6353 7.27908 20.7767 7.55267 20.8452 7.85364C20.9169 8.16951 20.9181 8.54685 20.8481 9.00715L20.843 9.04063V9.33561L21.0651 9.46563C21.252 9.56815 21.4006 9.68546 21.5145 9.81975C21.7044 10.0436 21.8272 10.3281 21.8791 10.6652C21.9328 11.012 21.9151 11.4248 21.8272 11.892C21.7259 12.4295 21.5622 12.8976 21.341 13.2805C21.1376 13.6334 20.8785 13.9262 20.5708 14.153C20.277 14.3686 19.928 14.5322 19.5333 14.6369C19.1509 14.7398 18.7149 14.7917 18.2367 14.7917H17.9286C17.7083 14.7917 17.4943 14.8737 17.3263 15.0207C17.1579 15.1708 17.0465 15.3758 17.0123 15.6L16.989 15.7305L16.599 18.2848L16.5814 18.3785C16.5767 18.4082 16.5686 18.423 16.5568 18.433C16.5463 18.4422 16.5311 18.4483 16.5164 18.4483H14.614Z"
          fill="#28356A"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M21.2761 9.07458C21.2644 9.15267 21.2508 9.23246 21.2356 9.31445C20.7136 12.0851 18.9273 13.0422 16.646 13.0422H15.4845C15.2055 13.0422 14.9703 13.2516 14.9269 13.536L14.1638 18.5393C14.1356 18.7261 14.2748 18.8944 14.4571 18.8944H16.5173C16.7612 18.8944 16.9684 18.7112 17.0069 18.4626L17.0271 18.3544L17.415 15.8102L17.4399 15.6707C17.4779 15.4211 17.6856 15.2378 17.9295 15.2378H18.2376C20.2336 15.2378 21.7961 14.4003 22.2528 11.9765C22.4435 10.964 22.3448 10.1185 21.84 9.52389C21.6873 9.34464 21.4977 9.1958 21.2761 9.07458Z"
          fill="#298FC2"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M20.7298 8.84956C20.65 8.82549 20.5677 8.80374 20.4833 8.78407C20.3984 8.76488 20.3115 8.7479 20.222 8.73299C19.9089 8.68069 19.5656 8.65588 19.1981 8.65588H16.0951C16.0186 8.65588 15.946 8.67372 15.8811 8.70598C15.7379 8.7771 15.6316 8.91714 15.6058 9.08857L14.9457 13.4101L14.9268 13.5361C14.9701 13.2516 15.2053 13.0423 15.4843 13.0423H16.6459C18.9271 13.0423 20.7134 12.0847 21.2354 9.31451C21.2511 9.23252 21.2642 9.15273 21.2759 9.07464C21.1438 9.00218 21.0008 8.94023 20.8467 8.88744C20.8087 8.87437 20.7694 8.86178 20.7298 8.84956Z"
          fill="#22284F"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M15.6056 9.08862C15.6314 8.91718 15.7377 8.77715 15.8809 8.70652C15.9462 8.67414 16.0184 8.6563 16.0948 8.6563H19.1979C19.5654 8.6563 19.9086 8.68123 20.2218 8.73353C20.3113 8.74831 20.3982 8.76542 20.4831 8.7846C20.5675 8.80415 20.6498 8.82603 20.7296 8.84998C20.7692 8.8622 20.8085 8.8749 20.8469 8.88749C21.0009 8.94028 21.1441 9.00272 21.2761 9.07469C21.4315 8.05082 21.2748 7.3537 20.7393 6.72245C20.1488 6.0274 19.0831 5.72998 17.7194 5.72998H13.7603C13.4817 5.72998 13.2441 5.9393 13.2011 6.22426L11.5521 17.0279C11.5196 17.2416 11.679 17.4344 11.8876 17.4344H14.3318L15.6056 9.08862Z"
          fill="#28356A"
        />
      </svg>
    ),
    sezzle: (
      <svg {...sharedSvgAttrs} width="96" height="24" viewBox="0 0 96 24">
        <g clipPath="url(#clipSezzle)">
          <path
            d="M1.62695 16.1392C2.14303 16.6554 2.75577 17.065 3.43016 17.3444C4.10456 17.6238 4.8274 17.7677 5.55739 17.7677C6.28738 17.7677 7.01022 17.6238 7.68461 17.3444C8.35901 17.065 8.97174 16.6554 9.48782 16.1392L9.69592 15.9311C10.7785 14.8485 8.61332 9.1564 9.69592 8.07019L1.62695 16.1392Z"
            fill="url(#paint0_linear)"
          />
          <path
            d="M9.90636 7.86203L9.69826 8.07013C8.61566 9.15273 10.7809 14.8448 9.69826 15.9298L17.766 7.86203C16.7236 6.82 15.3101 6.23462 13.8362 6.23462C12.3623 6.23462 10.9487 6.82 9.90636 7.86203Z"
            fill="url(#paint1_linear)"
          />
          <path
            d="M1.62743 8.27947C1.11118 8.79544 0.701651 9.40808 0.422244 10.0824C0.142836 10.7567 -0.000976562 11.4794 -0.000976562 12.2093C-0.000976562 12.9392 0.142836 13.6619 0.422244 14.3362C0.701651 15.0105 1.11118 15.6232 1.62743 16.1391L9.9069 7.85966C10.9489 6.81729 11.5343 5.40373 11.5343 3.92983C11.5343 2.45593 10.9489 1.04238 9.9069 0L1.62743 8.27947Z"
            fill="url(#paint2_linear)"
          />
          <path
            d="M9.48884 16.1391C8.44674 17.1817 7.86133 18.5954 7.86133 20.0695C7.86133 21.5436 8.44674 22.9574 9.48884 24L17.7683 15.7205C18.2846 15.2045 18.6941 14.5919 18.9735 13.9176C19.2529 13.2433 19.3967 12.5206 19.3967 11.7907C19.3967 11.0608 19.2529 10.338 18.9735 9.66374C18.6941 8.98945 18.2846 8.37681 17.7683 7.86084L9.48884 16.1391Z"
            fill="url(#paint3_linear)"
          />
          <path
            d="M49.9735 12.0434C49.7449 11.2606 49.3644 10.5305 48.8536 9.89472C48.3429 9.25898 47.7119 8.73006 46.9968 8.33817C45.5525 7.5467 43.8529 7.3614 42.272 7.82303C40.6911 8.28466 39.3583 9.35541 38.5668 10.7997C37.7753 12.244 37.59 13.9436 38.0517 15.5245V15.5402C38.1575 15.8938 38.3271 16.3461 38.3307 16.3533C38.8588 17.5899 39.7429 18.5329 41.0336 19.2402C42.3083 19.9369 43.7866 20.1652 45.2121 19.8854C46.6375 19.6057 47.9199 18.8357 48.8368 17.709L48.8873 17.6464C49.0915 17.3883 49.2749 17.1144 49.4358 16.8273C49.5618 16.5833 49.588 16.2999 49.5089 16.0369C49.4299 15.7739 49.2518 15.552 49.0121 15.4179C48.7725 15.2838 48.4902 15.2481 48.2247 15.3182C47.9592 15.3884 47.7314 15.559 47.5894 15.794C47.3293 16.2585 46.9802 16.6673 46.5621 16.9969C46.5301 17.0215 46.5003 17.0489 46.4731 17.0787C45.8425 17.546 45.0914 17.8233 44.3083 17.8779C43.5253 17.9324 42.743 17.762 42.0537 17.3866C41.4218 17.0404 40.8943 16.5311 40.526 15.9119L49.2614 13.3521C49.5291 13.2727 49.7545 13.0903 49.888 12.8449C50.0215 12.5996 50.0522 12.3113 49.9735 12.0434ZM44.0252 9.6845C44.7092 9.68681 45.3819 9.8596 45.9824 10.1872C46.5828 10.5149 47.0922 10.987 47.4643 11.561L47.5268 11.6608L39.9149 13.8934C39.9025 13.3457 39.9994 12.801 40.2002 12.2913C40.4009 11.7816 40.7014 11.317 41.0839 10.9249C41.4665 10.5328 41.9235 10.221 42.4282 10.0078C42.9328 9.7946 43.475 9.68428 44.0228 9.6833L44.0252 9.6845Z"
            fill="currentColor"
          />
          <path
            d="M61.1919 17.9928H55.4096L61.8751 8.49C61.935 8.40228 61.9699 8.29989 61.976 8.19385C61.982 8.08781 61.9591 7.98212 61.9096 7.88813C61.8602 7.79414 61.786 7.7154 61.6952 7.66037C61.6043 7.60535 61.5002 7.57611 61.394 7.57581H53.1675C52.9049 7.57581 52.6531 7.68011 52.4674 7.86576C52.2818 8.05142 52.1775 8.30322 52.1775 8.56578C52.1775 8.82834 52.2818 9.08014 52.4674 9.2658C52.6531 9.45146 52.9049 9.55576 53.1675 9.55576H58.6767L52.2124 19.0586C52.1524 19.1462 52.1174 19.2485 52.1113 19.3545C52.1052 19.4605 52.1281 19.5662 52.1776 19.6602C52.2271 19.7541 52.3014 19.8327 52.3923 19.8876C52.4832 19.9425 52.5873 19.9715 52.6935 19.9716H61.1907C61.4471 19.9626 61.6901 19.8545 61.8683 19.6699C62.0465 19.4853 62.1462 19.2388 62.1462 18.9822C62.1462 18.7256 62.0465 18.479 61.8683 18.2945C61.6901 18.1099 61.4471 18.0017 61.1907 17.9928H61.1919Z"
            fill="currentColor"
          />
          <path
            d="M74.0016 17.9928H68.2205L74.686 8.49002C74.746 8.4024 74.7809 8.30007 74.7871 8.19406C74.7932 8.08804 74.7703 7.98237 74.7207 7.88843C74.6712 7.79449 74.597 7.71584 74.5061 7.66097C74.4152 7.60609 74.311 7.57707 74.2049 7.57703H65.9819C65.7194 7.57703 65.4676 7.68133 65.2819 7.86698C65.0962 8.05264 64.9919 8.30444 64.9919 8.567C64.9919 8.82956 65.0962 9.08136 65.2819 9.26702C65.4676 9.45268 65.7194 9.55698 65.9819 9.55698H71.4911L65.0268 19.0598C64.9668 19.1474 64.9319 19.2497 64.9258 19.3557C64.9197 19.4618 64.9426 19.5674 64.9921 19.6614C65.0416 19.7553 65.1158 19.834 65.2067 19.8888C65.2976 19.9437 65.4018 19.9727 65.508 19.9728H74.0016C74.2641 19.9728 74.5159 19.8685 74.7016 19.6828C74.8872 19.4972 74.9915 19.2454 74.9915 18.9828C74.9915 18.7202 74.8872 18.4684 74.7016 18.2828C74.5159 18.0971 74.2641 17.9928 74.0016 17.9928Z"
            fill="currentColor"
          />
          <path
            d="M79.7107 2.2218C79.4482 2.22212 79.1966 2.32652 79.011 2.51211C78.8254 2.6977 78.721 2.94932 78.7207 3.21178V19.0189C78.7207 19.2815 78.825 19.5333 79.0107 19.7189C79.1963 19.9046 79.4481 20.0089 79.7107 20.0089C79.9732 20.0089 80.225 19.9046 80.4107 19.7189C80.5964 19.5333 80.7007 19.2815 80.7007 19.0189V3.21178C80.7003 2.94932 80.5959 2.6977 80.4103 2.51211C80.2248 2.32652 79.9731 2.22212 79.7107 2.2218Z"
            fill="currentColor"
          />
          <path
            d="M95.9486 12.0433C95.7815 11.4693 95.5319 10.9227 95.2076 10.4206C94.4808 9.29602 93.41 8.43651 92.1549 7.97023C90.8998 7.50395 89.5276 7.45586 88.2429 7.83316C86.6649 8.29569 85.3348 9.36513 84.5442 10.8069C83.7536 12.2488 83.5671 13.9453 84.0256 15.5244V15.5413C84.1314 15.8937 84.301 16.346 84.3046 16.3544C84.8327 17.591 85.7168 18.534 87.0075 19.2413C88.281 19.9352 89.757 20.1622 91.1801 19.883C92.6032 19.6038 93.884 18.8361 94.801 17.7125L94.8527 17.6463C95.0565 17.3886 95.2395 17.115 95.4 16.8283C95.526 16.5844 95.5522 16.301 95.4732 16.038C95.3941 15.775 95.216 15.5531 94.9764 15.419C94.7368 15.2849 94.4544 15.2492 94.189 15.3193C93.9235 15.3895 93.6957 15.5601 93.5536 15.7951C93.2935 16.2596 92.9444 16.6683 92.5263 16.998C92.494 17.0226 92.4638 17.05 92.4361 17.0798C91.8055 17.5471 91.0544 17.8244 90.2714 17.879C89.4884 17.9335 88.7061 17.7631 88.0167 17.3877C87.3849 17.0416 86.8571 16.5328 86.4879 15.9142L95.2328 13.352C95.5013 13.2733 95.7275 13.0913 95.8617 12.8458C95.9959 12.6004 96.0271 12.3117 95.9486 12.0433ZM89.9967 9.6844C90.6807 9.68671 91.3534 9.85949 91.9538 10.1871C92.5543 10.5148 93.0636 10.9869 93.4357 11.5609L93.4983 11.6607L85.8864 13.8933C85.8739 13.3456 85.9709 12.8009 86.1716 12.2912C86.3724 11.7815 86.6728 11.3169 87.0554 10.9248C87.438 10.5327 87.895 10.2209 88.3997 10.0077C88.9043 9.7945 89.4464 9.68417 89.9943 9.6832L89.9967 9.6844Z"
            fill="currentColor"
          />
          <path
            d="M34.7359 14.4683C34.4128 14.013 33.9795 13.6472 33.4765 13.4049C32.5912 12.9538 31.4412 12.7469 30.3406 12.5749L30.1661 12.5485C29.3927 12.4282 28.5928 12.3079 28.0791 12.0433C27.6906 11.846 27.5523 11.6247 27.5523 11.1928C27.5523 10.3508 28.4616 9.72649 29.6693 9.72649C31.1128 9.72649 31.9139 10.1475 32.8149 10.6972C32.9344 10.7782 33.0691 10.834 33.2108 10.8613C33.3525 10.8886 33.4983 10.8869 33.6393 10.8562C33.7803 10.8254 33.9136 10.7664 34.0312 10.6826C34.1487 10.5989 34.248 10.4921 34.323 10.3688C34.398 10.2455 34.4472 10.1082 34.4676 9.96537C34.488 9.82249 34.4791 9.67696 34.4416 9.5376C34.4041 9.39825 34.3386 9.26796 34.2492 9.15466C34.1598 9.04136 34.0483 8.94739 33.9215 8.87846C33.3758 8.52785 32.7944 8.23631 32.187 8.00877C31.3755 7.72484 30.5206 7.5852 29.6609 7.59618C28.5495 7.59618 27.5102 7.93299 26.7343 8.54766C26.3249 8.85714 25.9925 9.25696 25.763 9.71598C25.5335 10.175 25.4131 10.6808 25.4111 11.194C25.4111 12.433 25.9981 13.3857 27.1084 13.9474C27.924 14.36 28.8935 14.5104 29.8305 14.6559L30.0025 14.6824C32.4083 15.0589 33.1 15.4041 33.1 16.1932C33.1 17.1784 32.1136 17.8231 30.5871 17.8364H30.5318C29.4371 17.8601 28.3652 17.5219 27.4825 16.874C27.4007 16.8187 27.3309 16.7694 27.2612 16.7261C27.1427 16.6423 27.0085 16.5836 26.8666 16.5535C26.7247 16.5235 26.5781 16.5227 26.4359 16.5512C26.2937 16.5798 26.1588 16.6371 26.0395 16.7196C25.9202 16.8021 25.819 16.9081 25.742 17.0311C25.6651 17.1541 25.6141 17.2915 25.5922 17.4349C25.5702 17.5782 25.5778 17.7246 25.6144 17.865C25.651 18.0053 25.7158 18.1368 25.805 18.2512C25.8941 18.3656 26.0057 18.4606 26.1329 18.5304C26.1605 18.5473 26.2026 18.5761 26.2532 18.6086L26.282 18.6279C27.5211 19.5204 29.0146 19.9903 30.5414 19.9679H30.6148C31.938 19.9558 33.0783 19.5998 33.9071 18.9358C34.325 18.6114 34.6629 18.1955 34.8947 17.72C35.1266 17.2445 35.2464 16.7222 35.2447 16.1932C35.2557 15.5797 35.0781 14.9776 34.7359 14.4683Z"
            fill="currentColor"
          />
        </g>
        <defs>
          <linearGradient
            id="paint0_linear"
            x1="9.24243"
            y1="16.3725"
            x2="5.34147"
            y2="11.7234"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#CE5DCB" />
            <stop offset="0.21" stopColor="#C558CC" />
            <stop offset="0.55" stopColor="#AC4ACF" />
            <stop offset="0.98" stopColor="#8534D4" />
            <stop offset="1" stopColor="#8333D4" />
          </linearGradient>
          <linearGradient
            id="paint1_linear"
            x1="9.38551"
            y1="11.0822"
            x2="17.766"
            y2="11.0822"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0.02" stopColor="#FF5667" />
            <stop offset="0.66" stopColor="#FC8B82" />
            <stop offset="1" stopColor="#FBA28E" />
          </linearGradient>
          <linearGradient
            id="paint2_linear"
            x1="-0.00127994"
            y1="8.06897"
            x2="11.5344"
            y2="8.06897"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#00B874" />
            <stop offset="0.51" stopColor="#29D3A2" />
            <stop offset="0.68" stopColor="#53DFB6" />
            <stop offset="1" stopColor="#9FF4D9" />
          </linearGradient>
          <linearGradient
            id="paint3_linear"
            x1="7.86133"
            y1="15.9298"
            x2="19.397"
            y2="15.9298"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#FCD77E" />
            <stop offset="0.52" stopColor="#FEA500" />
            <stop offset="1" stopColor="#FF5B00" />
          </linearGradient>
          <clipPath id="clipSezzle">
            <rect width="95.9904" height="24" fill="white" />
          </clipPath>
        </defs>
      </svg>
    ),
    facebook: (
      <svg {...sharedSvgAttrs} width="48" height="48" viewBox="0 0 48 48">
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M25.9926 37.2V24.5985H29.4711L29.9321 20.256H25.9926L25.9985 18.0825C25.9985 16.9499 26.1061 16.343 27.7328 16.343H29.9075V12H26.4284C22.2495 12 20.7787 14.1066 20.7787 17.6492V20.2565H18.1738V24.599H20.7787V37.2H25.9926Z"
          fill="currentColor"
        />
      </svg>
    ),
    instagram: (
      <svg {...sharedSvgAttrs} width="48" height="48" viewBox="0 0 48 48">
        <g clipPath="url(#clipInstgram)">
          <path
            d="M32.1998 12H16.2005C15.1392 12 14.122 12.4219 13.3721 13.1719C12.6221 13.9219 12.2002 14.9391 12.2002 16.0003V31.9996C12.2002 33.0609 12.6221 34.0781 13.3721 34.828C14.1221 35.578 15.1393 35.9999 16.2005 35.9999H32.1998C33.2611 35.9999 34.2783 35.578 35.0282 34.828C35.7782 34.078 36.2001 33.0608 36.2001 31.9996V16.0003C36.2001 14.939 35.7782 13.9218 35.0282 13.1719C34.2782 12.4219 33.261 12 32.1998 12ZM34.1995 31.9997H34.2004C34.2004 32.5303 33.9895 33.0394 33.6145 33.4144C33.2395 33.7894 32.7304 34.0003 32.1998 34.0003H16.2005C15.0951 34.0003 14.1999 33.105 14.1999 31.9997V16.0003C14.1999 14.895 15.0952 13.9997 16.2005 13.9997H32.1998C32.7304 13.9997 33.2395 14.2106 33.6145 14.5856C33.9895 14.9606 34.2004 15.4697 34.2004 16.0003L34.1995 31.9997Z"
            fill="currentColor"
          />
          <path
            d="M24.2002 18C22.6093 18 21.0831 18.6319 19.958 19.7578C18.8321 20.8828 18.2002 22.4091 18.2002 24C18.2002 25.591 18.8321 27.1172 19.958 28.2423C21.083 29.3682 22.6093 30.0001 24.2002 30.0001C25.7912 30.0001 27.3174 29.3682 28.4425 28.2423C29.5684 27.1173 30.2003 25.591 30.2003 24C30.2003 22.4091 29.5684 20.8829 28.4425 19.7578C27.3175 18.6319 25.7912 18 24.2002 18ZM24.2002 28.0003C23.139 28.0003 22.1218 27.5784 21.3718 26.8284C20.6219 26.0784 20.2 25.0613 20.2 24C20.2 22.9388 20.6218 21.9216 21.3718 21.1716C22.1218 20.4217 23.139 19.9998 24.2002 19.9998C25.2615 19.9998 26.2787 20.4216 27.0286 21.1716C27.7786 21.9216 28.2005 22.9388 28.2005 24C28.2005 25.0613 27.7786 26.0785 27.0286 26.8284C26.2786 27.5784 25.2615 28.0003 24.2002 28.0003Z"
            fill="currentColor"
          />
          <path
            d="M32.1992 17.5004C32.1992 18.3282 31.528 19.0004 30.6992 19.0004C29.8714 19.0004 29.1992 18.3282 29.1992 17.5004C29.1992 16.6716 29.8714 16.0004 30.6992 16.0004C31.528 16.0004 32.1992 16.6716 32.1992 17.5004Z"
            fill="currentColor"
          />
        </g>
        <defs>
          <clipPath id="clipInstgram">
            <rect
              width="24"
              height="24"
              fill="currentColor"
              transform="translate(12.2002 12)"
            />
          </clipPath>
        </defs>
      </svg>
    ),
    twitter: (
      <svg {...sharedSvgAttrs} width="48" height="48" viewBox="0 0 48 48">
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M24.0095 20.0173L24.0581 20.8188L23.248 20.7207C20.2993 20.3445 17.7233 19.0687 15.5361 16.926L14.4668 15.8628L14.1914 16.6479C13.6081 18.3981 13.9807 20.2463 15.1959 21.4894C15.8439 22.1764 15.6981 22.2745 14.5802 21.8656C14.1914 21.7348 13.8511 21.6366 13.8187 21.6857C13.7053 21.8002 14.0942 23.2886 14.402 23.8775C14.8232 24.6953 15.6819 25.4968 16.6216 25.9711L17.4155 26.3473L16.4758 26.3636C15.5685 26.3636 15.5361 26.38 15.6333 26.7235C15.9573 27.7867 17.2373 28.9153 18.663 29.4059L19.6675 29.7494L18.7926 30.2728C17.4965 31.0252 15.9735 31.4505 14.4506 31.4832C13.7215 31.4996 13.1221 31.565 13.1221 31.6141C13.1221 31.7776 15.0987 32.6936 16.249 33.0534C19.6999 34.1166 23.7989 33.6586 26.8771 31.8431C29.0643 30.5509 31.2515 27.9829 32.2722 25.4968C32.8231 24.1719 33.3739 21.7511 33.3739 20.5898C33.3739 19.8374 33.4226 19.7393 34.3298 18.8397C34.8645 18.3163 35.3667 17.7438 35.4639 17.5802C35.626 17.2695 35.6098 17.2695 34.7835 17.5475C33.4063 18.0382 33.2119 17.9728 33.8924 17.2368C34.3946 16.7133 34.9941 15.7647 34.9941 15.4866C34.9941 15.4375 34.7511 15.5193 34.4756 15.6665C34.184 15.8301 33.536 16.0754 33.0499 16.2227L32.175 16.5007L31.3812 15.961C30.9437 15.6665 30.3281 15.3394 30.004 15.2413C29.1778 15.0123 27.914 15.045 27.1688 15.3067C25.1436 16.0427 23.8637 17.9401 24.0095 20.0173Z"
          fill="currentColor"
        />
      </svg>
    ),
    tiktok: (
      <svg
        {...sharedSvgAttrs}
        width="48"
        height="48"
        style={{ padding: '5px' }}
        viewBox="-4.004985 -7.600025 34.70987 45.60015"
      >
        <path
          fill="currentColor"
          d="M10.4 11.9999v-1.2c-.4-.1-.8-.0999-1.2-.0999-5.1 0-9.2 4.0999-9.2 9.1999 0 3.1 1.6 5.9 3.9 7.5l-.1-.1C2.3 25.6999 1.5 23.5 1.5 21.2c0-5.1 4-9.1001 8.9-9.2001z"
        />
        <path
          fill="currentColor"
          d="M10.6002 25.4c2.3 0 4.1-1.8 4.2-4.1v-20h3.6c-.1-.4-.1-.8-.1-1.3h-5v20c-.1 2.2-1.9 4-4.2 4-.7 0-1.4-.2-1.9-.5.8 1.1 2 1.9 3.4 1.9zM25.3 8.1V6.9c-1.4 0-2.7-.4-3.8-1.1 1 1.1 2.3 2 3.8 2.3z"
        />
        <path
          fill="currentColor"
          d="M21.4999 5.8c-1.1-1.2-1.7-2.8-1.7-4.6h-1.4c.4 2 1.6 3.6 3.1 4.6zM9.2 15.5999c-2.3 0-4.2 1.9-4.2 4.2 0 1.6 1 3 2.3 3.7-.5-.7-.8-1.5-.8-2.4 0-2.3 1.9-4.2 4.2-4.2.4 0 .8.1 1.2.2v-5.1c-.4-.1-.8-.1-1.2-.1h-.2v3.8c-.5 0-.9-.1-1.3-.1z"
        />
        <path
          fill="currentColor"
          d="M25.2999 8.1001v3.8c-2.6 0-5-.8-6.9-2.2v10.2c0 5.1-4.1 9.2-9.2 9.2-2 0-3.8-.6-5.3-1.6 1.7 1.8 4.1 2.9 6.7 2.9 5.1 0 9.2-4.1 9.2-9.2v-10.2c2 1.4 4.4 2.2 6.9 2.2v-5c-.4 0-.9 0-1.4-.1z"
        />
        <path
          fill="currentColor"
          d="M18.3999 19.8999v-10.2c2 1.4001 4.4 2.2 6.9 2.2v-3.9c-1.5-.3-2.8-1.1-3.8-2.2-1.6-1-2.7-2.7-3-4.6h-3.7V21.2c-.1 2.2-1.9 4-4.2 4-1.4 0-2.6-.7001-3.4-1.7001-1.3-.6-2.2-2-2.2-3.6 0-2.3 1.9-4.1999 4.2-4.1999.4 0 .8.0999 1.2.1999v-3.9c-5 .1-9 4.2001-9 9.2001 0 2.4.9 4.5999 2.5 6.2999 1.5 1 3.3 1.7001 5.3 1.7001 5.1-.1 9.2-4.3001 9.2-9.3001z"
        />
      </svg>
    ),
    nav: (
      <svg {...sharedSvgAttrs} viewBox="0 0 9 4">
        <path
          d="M4.5 4L0.602886 0.625L8.39711 0.625001L4.5 4Z"
          fill="currentColor"
        />
      </svg>
    ),
    play: (
      <svg {...sharedSvgAttrs} viewBox="0 0 40 40">
        <title>Play</title>
        <circle opacity="0.8" cx="20" cy="20" r="14" fill="white" />
        <path
          d="M27.5 19.134C28.1667 19.5189 28.1667 20.4811 27.5 20.866L17 26.9282C16.3333 27.3131 15.5 26.832 15.5 26.0622L15.5 13.9378C15.5 13.168 16.3333 12.6869 17 13.0718L27.5 19.134Z"
          fill="currentColor"
        />
      </svg>
    ),
  };

  return iconSets[set][type] ?? defaultIcons[type] ?? null;
};

Icons.propTypes = {
  type: PropTypes.string.isRequired,
  ariaLabel: PropTypes.string,
  className: PropTypes.string,
  onClick: PropTypes.func,
};

export default Icons;
